import { normalizeWallet } from "@hupper/shared";

/** Base URL of the Hupper REST API (VITE_API_URL, trailing slash trimmed). */
export const API_URL = (import.meta.env.VITE_API_URL ?? "http://localhost:4000").replace(/\/$/, "");

export class ApiError extends Error {
  constructor(public status: number, public detail: unknown) {
    super(typeof detail === "string" ? detail : `api error ${status}`);
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`${API_URL}${path}`, {
    ...init,
    headers: { "content-type": "application/json", ...(init.headers ?? {}) },
  });
  const text = await res.text();
  const body = text ? JSON.parse(text) : null;
  if (!res.ok) throw new ApiError(res.status, body?.error ?? body ?? res.statusText);
  return body as T;
}

export interface AuthResponse {
  token: string;
  mode: "anon" | "privy" | "siwe";
  wallet?: string | null;
  email?: string | null;
}

export const authApi = {
  anon: () => request<AuthResponse>("/api/auth/anon", { method: "POST" }),
  privy: (accessToken: string, displayName?: string | null, referrerCode?: string | null) =>
    request<AuthResponse>("/api/auth/privy", {
      method: "POST",
      body: JSON.stringify({
        accessToken,
        displayName: displayName ?? undefined,
        referrerCode: referrerCode ?? undefined,
      }),
    }),
  siweNonce: () => request<{ nonce: string }>("/api/auth/siwe/nonce"),
  siweVerify: (message: string, signature: string) =>
    request<AuthResponse>("/api/auth/siwe/verify", {
      method: "POST",
      body: JSON.stringify({ message, signature }),
    }),
  me: (token: string) =>
    request<{ auth: Record<string, unknown> }>("/api/auth/me", {
      headers: { Authorization: `Bearer ${token}` },
    }),
};

export function authedFetch(token: string, path: string, init: RequestInit = {}) {
  return fetch(`${API_URL}${path}`, {
    ...init,
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${token}`,
      ...(init.headers ?? {}),
    },
  });
}

async function authedJson<T>(token: string, path: string, init: RequestInit = {}): Promise<T> {
  const res = await authedFetch(token, path, init);
  const text = await res.text();
  const body = text ? JSON.parse(text) : null;
  if (!res.ok) throw new ApiError(res.status, body?.error ?? body ?? res.statusText);
  return body as T;
}

export interface FeedEvent {
  id: string;
  kind:
    // HupperVault (legacy Pong):
    | "Deposited"
    | "Withdrawn"
    | "MatchStarted"
    | "MatchSettled"
    | "MatchCancelled"
    // HupperGames (Snake & future Polymarket-style games):
    | "StakeLocked"
    | "SessionSettled"
    | "StakeTransferred";
  blockNumber: number;
  txHash: string;
  timestamp: number;
  data: Record<string, string | number | boolean | null>;
}

export interface LeaderboardRow {
  address: string;
  matchesPlayed: number;
  matchesWon: number;
  winRate: number;
  totalWinnings: string;
  volume: string; // total wagered across all matches (minor units)
}

export const feedApi = {
  recent: (limit = 200) =>
    request<{ events: FeedEvent[] }>(`/api/feed/recent?limit=${limit}`),
  leaderboard: (limit = 100) =>
    request<{ leaderboard: LeaderboardRow[] }>(`/api/feed/leaderboard?limit=${limit}`),
  userHistory: (address: string, limit = 100) =>
    request<{ events: FeedEvent[] }>(`/api/feed/users/${address}/history?limit=${limit}`),
  status: () =>
    request<{ latestBlock: number; events: number; users: number }>("/api/feed/status"),
};

/**
 * One external wallet cashflow — a USDC deposit (money in) or withdrawal
 * (money out), excluding the stake/payout transfers that go to the game
 * contracts. `amount` is USDC base units (6 decimals) as a string.
 */
export interface WalletTransfer {
  id: string;
  ts: number;
  blockNumber: number;
  txHash: string;
  direction: "in" | "out";
  amount: string;
  counterparty: string;
}

export const walletApi = {
  /**
   * The caller's own deposits + withdrawals (owner-only — the wallet is
   * read from the token, never a param). `synced=false` means the initial
   * scan is still running and the caller should poll again shortly.
   */
  transfers: (token: string) =>
    authedJson<{ transfers: WalletTransfer[]; synced: boolean }>(token, "/api/wallet/transfers"),
};

/** One recorded match in the server-side stats store (per-game). */
export interface StatsRecentRow {
  resultId: string;
  ts: number;
  outcome: "win" | "loss";
  opponent: string | null;
  stake: string;
  ratingDelta: number | null;
}

/** Server-side ratings + telemetry for one wallet+game (Elo lives here). */
export interface ServerGameStats {
  rating: number | null;
  games: number;
  rank: number | null;
  poolSize: number;
  matches: number;
  wins: number;
  losses: number;
  /** Accumulated game-specific telemetry (kills, bestWave, …). */
  extra: Record<string, number>;
  recent: StatsRecentRow[];
}

/** Community context for one game + the caller's standing. */
export interface GameSummary {
  game: string;
  matches: number;
  volume: string;
  players: number;
  hasRating: boolean;
  top: { wallet: string; rating: number; games: number }[];
  you: { rating: number | null; rank: number | null; games: number } | null;
}

export const statsApi = {
  /** Per-game ratings + telemetry for one wallet. */
  user: (wallet: string) =>
    request<{ games: Record<string, ServerGameStats> }>(`/api/stats/user/${wallet}`),
  /** Community context for one game (matches, volume, top players, your rank). */
  gameSummary: (game: string, wallet?: string) =>
    request<GameSummary>(
      `/api/stats/game/${game}/summary${wallet ? `?wallet=${wallet}` : ""}`,
    ),
};

export interface DelegatedTxRequest {
  to: `0x${string}`;
  data?: `0x${string}`;
  value?: `0x${string}`;
}

export interface DirectoryUser {
  wallet: string;
  handle: string | null;
  online: boolean;
  avatarVersion?: number;
}

/** Public display fields for a wallet (leaderboard / feed). */
export interface PublicUser {
  wallet: string;
  handle: string | null;
  avatarVersion: number;
  /** ISO-3166 alpha-2 country code from the login IP, or null. */
  country: string | null;
}

/** Full editable / public profile (GET /me, by-wallet, PATCH responses). */
export interface UserProfile {
  wallet: string;
  handle: string | null;
  bio: string | null;
  online: boolean;
  avatarVersion: number;
  handleCustom: boolean;
  /** Wall-clock ms of the last custom rename, or null. */
  handleChangedAtMs: number | null;
  /** Rename cooldown window (ms) — 14 days. */
  handleCooldownMs: number;
}

/**
 * Absolute URL for a user's uploaded avatar, cache-busted by version.
 * Returns null when the user has no custom photo (caller renders the
 * generated fallback).
 */
export function avatarUrl(wallet: string | null | undefined, version: number | undefined): string | null {
  if (!wallet || !version || version <= 0) return null;
  // normalizeWallet, not raw toLowerCase: EVM 0x lowercases exactly as before, but
  // Solana base58 is case-sensitive and must pass through unchanged — the avatar
  // file is keyed by normalizeWallet server-side, so a lowercased base58 404s.
  return `${API_URL}/api/users/${normalizeWallet(wallet)}/avatar?v=${version}`;
}

export const usersApi = {
  /**
   * Search the server-side player directory by handle (case-insensitive
   * substring) or wallet prefix. Empty query returns the 20 most-
   * recently-seen players. Excludes the caller's own wallet.
   */
  search: (token: string, query: string, limit = 20) =>
    authedJson<{ users: DirectoryUser[] }>(
      token,
      `/api/users/search?q=${encodeURIComponent(query)}&limit=${limit}`,
    ),

  /**
   * Public (no-auth) batch lookup of display fields for a set of explicit
   * wallets — used by the leaderboard / feed to show each player's chosen
   * name + avatar instead of a bare address. Chunked so a long wallet list
   * never overflows the query string; unknown wallets are simply omitted.
   */
  async publicProfiles(wallets: string[]): Promise<PublicUser[]> {
    // normalizeWallet, not raw toLowerCase: base58 (Solana) is case-sensitive, so
    // lowercasing forks a phantom wallet the server can't match. EVM 0x still
    // collapses to one canonical lowercase key exactly as before.
    const unique = [...new Set(wallets.map((w) => normalizeWallet(w)).filter(Boolean))];
    if (unique.length === 0) return [];
    const CHUNK = 100;
    const chunks: string[][] = [];
    for (let i = 0; i < unique.length; i += CHUNK) chunks.push(unique.slice(i, i + CHUNK));
    const results = await Promise.all(
      chunks.map((chunk) =>
        request<{ users: PublicUser[] }>(
          `/api/users/public?wallets=${encodeURIComponent(chunk.join(","))}`,
        ),
      ),
    );
    return results.flatMap((r) => r.users);
  },

  /** The caller's own profile + current email (from the verified token). */
  me: (token: string) =>
    authedJson<{ profile: UserProfile; email: string | null }>(token, "/api/users/me"),

  /** Public profile of any wallet (handle + bio + avatar). */
  byWallet: (token: string, wallet: string) =>
    authedJson<{ user: UserProfile }>(token, `/api/users/by-wallet/${wallet}`),

  /** Update username and/or bio. Throws ApiError (409/429/400) on rejects. */
  updateProfile: (token: string, patch: { handle?: string; bio?: string }) =>
    authedJson<{ profile: UserProfile }>(token, "/api/users/me", {
      method: "PATCH",
      body: JSON.stringify(patch),
    }),

  /** Upload a JPEG avatar (already resized client-side). */
  uploadAvatar: (token: string, jpeg: Blob) =>
    authedJson<{ profile: UserProfile }>(token, "/api/users/me/avatar", {
      method: "POST",
      headers: { "content-type": "image/jpeg" },
      body: jpeg,
    }),

  /** Remove the uploaded avatar (revert to generated). */
  deleteAvatar: (token: string) =>
    authedJson<{ profile: UserProfile }>(token, "/api/users/me/avatar", { method: "DELETE" }),

  /** Delete the account (tombstone + free handle + best-effort Privy delete). */
  deleteAccount: (token: string) =>
    authedJson<{ ok: boolean; privyDeleted: boolean }>(token, "/api/users/me", {
      method: "DELETE",
    }),
};

/** A friend / pending-request counterparty (enriched with directory profile). */
export interface SocialUser {
  wallet: string;
  handle: string | null;
  online: boolean;
  avatarVersion: number;
  /** `since` for friends, request timestamp for pending. */
  ts: number;
}

/** A custom-match challenge counterparty, enriched like SocialUser. */
export type InviteGame = "fps" | "pong" | "math" | "td";
export type InviteStatus = "pending" | "accepted" | "declined" | "cancelled" | "expired" | "settled";
export interface SocialMatchInvite extends SocialUser {
  /** Server-generated invite id. */
  id: string;
  game: InviteGame;
  /** Integer USD stake per player. */
  stake: number;
  status: InviteStatus;
  /** `cm:<id>` — present once accepted; pairs both players into the room. */
  customMatchId?: string | null;
}

/** A staff → player message, surfaced in the notification bell. */
export interface StaffMessage {
  id: string;
  body: string;
  createdAt: number;
  readAt: number | null;
}

export interface SocialState {
  friends: SocialUser[];
  incoming: SocialUser[];
  outgoing: SocialUser[];
  /** Incoming custom-match challenges (notifications). */
  matchInvitesIn?: SocialMatchInvite[];
  /** The caller's outgoing challenges (pending + freshly accepted). */
  matchInvitesOut?: SocialMatchInvite[];
  /** Staff messages addressed to the caller (notification bell). */
  staffMessages?: StaffMessage[];
}

/** Returned by accept — drives routing both players into the private match. */
export interface MatchCheckIn {
  game: InviteGame;
  stakeAmount: string;
  customMatchId: string;
  opponent: string;
}

/** Friends + friend-requests, synced across users via the backend. Every
 *  mutation returns the caller's fresh state so the client can apply it. */
export const socialApi = {
  get: (token: string) => authedJson<SocialState>(token, "/api/social"),
  sendRequest: (token: string, to: string) =>
    authedJson<{ result: string } & SocialState>(token, "/api/social/requests", {
      method: "POST",
      body: JSON.stringify({ to }),
    }),
  accept: (token: string, from: string) =>
    authedJson<SocialState>(token, `/api/social/requests/${from}/accept`, { method: "POST" }),
  decline: (token: string, from: string) =>
    authedJson<SocialState>(token, `/api/social/requests/${from}/decline`, { method: "POST" }),
  cancel: (token: string, to: string) =>
    authedJson<SocialState>(token, `/api/social/requests/${to}`, { method: "DELETE" }),
  removeFriend: (token: string, wallet: string) =>
    authedJson<SocialState>(token, `/api/social/friends/${wallet}`, { method: "DELETE" }),
  markStaffMessageRead: (token: string, id: string) =>
    authedJson<SocialState>(token, `/api/social/staff-messages/${id}/read`, { method: "POST" }),
};

/** Custom match invites (friend-to-friend challenges). */
export const matchInvitesApi = {
  create: (token: string, to: string, game: InviteGame, stake: number) =>
    authedJson<{ invite: { id: string; to: string; game: InviteGame; stake: number; status: InviteStatus } }>(
      token,
      "/api/match-invites",
      { method: "POST", body: JSON.stringify({ to, game, stake }) },
    ),
  accept: (token: string, id: string) =>
    // The accept response carries the bare invite (no enriched SocialUser
    // profile fields) plus the check-in used to route into the room.
    authedJson<{
      invite: { id: string; game: InviteGame; stake: number; status: InviteStatus; customMatchId?: string };
      matchCheckIn: MatchCheckIn;
    }>(token, `/api/match-invites/${id}/accept`, { method: "POST" }),
  decline: (token: string, id: string) =>
    authedJson<{ ok: boolean }>(token, `/api/match-invites/${id}/decline`, { method: "POST" }),
  cancel: (token: string, id: string) =>
    authedJson<{ ok: boolean }>(token, `/api/match-invites/${id}`, { method: "DELETE" }),
};

export interface ReferralStats {
  code: string;
  cutBps: number;
  /** Base-unit decimals: 9 (SOL lamports) on Solana, 6 (USDC) on EVM. */
  decimals: number;
  /** Currency of the credited amounts. */
  unit: "SOL" | "USDC";
  invited: number;
  active: number;
  /** Native-unit string ("0.0027" SOL / "12.34" USDC). */
  earned: string;
  pending: string;
  paid: string;
  /** Raw base units (lamports / USDC minor) for lamports→SOL→USD conversion. */
  earnedMinor: string;
  pendingMinor: string;
  paidMinor: string;
  history: Array<{
    referee: string;
    amount: string;
    amountMinor: string;
    blockNumber: number;
    txHash: string;
    creditedAtMs: number;
    paidTxHash: string | null;
    paidAtMs: number | null;
  }>;
}

export const referralsApi = {
  me: (token: string) =>
    authedJson<ReferralStats>(token, "/api/referrals/me"),
};

export const sessionApi = {
  signMessage: (token: string, message: string) =>
    authedJson<{ signature: string; encoding: string }>(
      token,
      "/api/session/sign-message",
      { method: "POST", body: JSON.stringify({ message }) },
    ),
  sendTx: (token: string, tx: DelegatedTxRequest) =>
    authedJson<{ hash?: string; transactionId?: string }>(
      token,
      "/api/session/send-tx",
      { method: "POST", body: JSON.stringify(tx) },
    ),
  /** Quick play: backend signs a wallet→Vault deposit via the Privy session
   *  signer (no user pop-up). `lamports` is a stringified integer. */
  solanaDeposit: (token: string, lamports: string) =>
    authedJson<{ hash: string }>(token, "/api/session/solana/deposit", {
      method: "POST",
      body: JSON.stringify({ lamports }),
    }),
};

// ─── Tournaments ────────────────────────────────────────────────────────────

/** A bracket match as served by the API (mirrors @hupper/shared BracketMatch). */
export interface TournamentMatch {
  id: string;
  round: number;
  index: number;
  playerA: string | null;
  playerB: string | null;
  winner: string | null;
  status: "pending" | "ready" | "playing" | "complete" | "bye";
  nextMatchId: string | null;
  nextSlot: "A" | "B" | null;
}

export interface TournamentBracket {
  rounds: TournamentMatch[][];
  size: number;
  seeds: Record<string, number>;
}

export type TournamentStatus = "filling" | "live" | "settling" | "complete" | "cancelled";
export type TournamentGame = "fps" | "pong" | "snake" | "td" | "math";

export interface TournamentSummary {
  id: string;
  templateId: string;
  game: TournamentGame;
  title: string;
  description: string;
  entry: number;
  currency: "USDT";
  minPlayers: number;
  maxPlayers: number;
  feeBps: number;
  /** Platform vault bonus added to the prize pool (whole dollars; 0 if none). */
  bonus: number;
  bonusUsd: number;
  status: TournamentStatus;
  startsAt: number;
  secondsToStart: number;
  playerCount: number;
  pot: string;
  potUsd: number;
  paidPositions: number;
  splitPct: number[];
  champion: string | null;
  escrowConfigured: boolean;
}

export interface CreateTournamentInput {
  game: "math" | "td";
  title: string;
  description?: string;
  entry: number;
  minPlayers: number;
  maxPlayers: number;
  startsInSeconds: number;
  roundIntervalSeconds?: number;
  matchDeadlineSeconds?: number;
  feeBps?: number;
}

export interface TournamentEntrant {
  wallet: string;
  seat: number;
  handle: string | null;
  avatarVersion: number;
  country: string | null;
  escrow: "onchain" | "offchain";
}

export interface TournamentPrize {
  place: number;
  amount: string;
  amountUsd: number;
  pct: number;
}

export interface TournamentPayoutRow {
  wallet: string;
  place: number;
  amount: string;
  fee: string;
  credited?: "onchain" | "offchain";
  settleTx?: string;
}

export interface TournamentRefundRow {
  wallet: string;
  credited: "onchain" | "offchain";
  refundTx?: string;
}

export interface TournamentDetail extends TournamentSummary {
  currentRound: number;
  roundStartedAtMs: number | null;
  nextRoundAtMs: number | null;
  cancelReason: string | null;
  entrants: TournamentEntrant[];
  bracket: TournamentBracket | null;
  projectedPrizes: TournamentPrize[];
  payouts: TournamentPayoutRow[];
  refunds: TournamentRefundRow[];
}

export interface TournamentCheckIn {
  game: TournamentGame;
  stakeAmount: string;
  tournamentMatchId: string;
  opponent: string | null;
}

export const tournamentsApi = {
  list: () =>
    request<{ tournaments: TournamentSummary[]; escrowConfigured: boolean }>("/api/tournaments"),
  detail: (id: string) =>
    request<{ tournament: TournamentDetail }>(`/api/tournaments/${id}`),
  join: (token: string, id: string) =>
    authedJson<{ tournament: TournamentDetail }>(token, `/api/tournaments/${id}/join`, {
      method: "POST",
    }),
  leave: (token: string, id: string) =>
    authedJson<{ tournament: TournamentDetail }>(token, `/api/tournaments/${id}/leave`, {
      method: "POST",
    }),
  checkIn: (token: string, id: string, matchId: string) =>
    authedJson<TournamentCheckIn>(
      token,
      `/api/tournaments/${id}/matches/${matchId}/check-in`,
      { method: "POST" },
    ),
  adminStatus: (token: string) =>
    authedJson<{ isAdmin: boolean }>(token, "/api/tournaments/admin/status"),
  create: (token: string, input: CreateTournamentInput) =>
    authedJson<{ tournament: TournamentDetail }>(token, "/api/tournaments", {
      method: "POST",
      body: JSON.stringify(input),
    }),
};

// ─── Admin metrics ──────────────────────────────────────────────────────────

export interface MetricsMoney {
  minor: string;
  usd: number;
}
export interface MetricsWindow {
  today: MetricsMoney;
  d7: MetricsMoney;
  d30: MetricsMoney;
  ytd: MetricsMoney;
  all: MetricsMoney;
}
export interface MetricsOverview {
  generatedAt: number;
  escrowConfigured: boolean;
  presence: { online: number; inGame: number | null; inGamePerGame: Record<string, number> | null };
  users: {
    total: number;
    new1d: number; new7d: number; new30d: number;
    activeLogin1d: number; activeLogin7d: number; activeLogin30d: number;
  };
  activeUsers: { dau: number; wau: number; mau: number; allTime: number };
  volume: MetricsWindow;
  fees: MetricsWindow;
  matches: { today: number; d7: number; d30: number; ytd: number; all: number };
  perGame: { game: string; matches: number; volume: MetricsMoney; fee: MetricsMoney }[];
  dailyVolumeUsd: { date: string; usd: number }[];
  onchainFeesAllTime: string;
  onchainFeesAllTimeUsd: number;
  tournaments: { total: number; byStatus: Record<string, number>; activePotUsd: number; activeEntrants: number };
}

export const metricsApi = {
  overview: (token: string) => authedJson<MetricsOverview>(token, "/api/metrics/overview"),
};

// ─── Admin moderation ───────────────────────────────────────────────────────

export interface AdminUserRow {
  wallet: string;
  handle: string | null;
  country: string | null;
  avatarVersion: number;
  createdAtMs: number;
  lastSeenAtMs: number;
  suspended: boolean;
  suspendedAtMs: number | null;
  suspendReason: string | null;
}

/** Per-game stats line in the admin user-detail drawer. */
export interface AdminUserGameStat {
  game: string;
  matches: number;
  wins: number;
  losses: number;
  rating: number | null;
  rank: number | null;
  wageredUsd: number;
  wonUsd: number;
}

/** One recent match in the admin user-detail drawer. */
export interface AdminUserRecentMatch {
  game: string;
  ts: number;
  outcome: "win" | "loss";
  opponent: string | null;
  opponentHandle: string | null;
  stakeUsd: number;
  netUsd: number;
  ratingDelta: number | null;
}

/** Full per-user dossier behind a clicked row in the admin Users tab. */
export interface AdminUserDetail {
  wallet: string;
  handle: string | null;
  country: string | null;
  email: string | null;
  lastIp: string | null;
  lastIpAtMs: number | null;
  avatarVersion: number;
  privyId: string | null;
  createdAtMs: number;
  lastSeenAtMs: number;
  suspended: boolean;
  suspendedAtMs: number | null;
  suspendReason: string | null;
  totals: { matches: number; wins: number; losses: number; wageredUsd: number; wonUsd: number; netUsd: number };
  games: AdminUserGameStat[];
  recent: AdminUserRecentMatch[];
}

export type ReportStatus = "open" | "reviewing" | "actioned" | "dismissed";
export type ReportReason = "cheating" | "abuse" | "harassment" | "scam" | "other";

export interface AdminReport {
  id: string;
  reporter: string;
  target: string;
  reason: ReportReason;
  detail: string;
  createdAtMs: number;
  status: ReportStatus;
  resolvedAtMs: number | null;
  resolvedBy: string | null;
  resolution: string | null;
  reporterHandle: string | null;
  targetHandle: string | null;
  targetSuspended: boolean;
}

// ─── Admin RBAC (staff roles & permissions) ─────────────────────────────────

export type Permission =
  | "view_metrics"
  | "view_live_matches"
  | "manage_live_matches"
  | "view_users"
  | "suspend_users"
  | "view_reports"
  | "resolve_reports"
  | "manage_tournaments"
  | "fund_tournaments"
  | "manage_arena"
  | "message_users";

/** The caller's own effective access — drives which dashboard tabs render. */
export interface AdminMe {
  wallet: string;
  isSuperAdmin: boolean;
  permissions: Permission[];
}

export interface StaffMember {
  wallet: string;
  permissions: Permission[];
  role: string | null;
  updatedAtMs: number;
  updatedBy: string;
}

export interface PermissionCatalog {
  permissions: { key: Permission; label: string }[];
  roles: Record<string, Permission[]>;
}

// ─── Admin live matches (read-only view of in-progress rooms) ────────────────

export interface AdminLivePlayer {
  sessionId: string;
  wallet: string | null;
  /** In-game display name where the room tracks one (FPS / Snake). */
  name: string | null;
  /** Platform handle resolved by the API from the wallet. */
  handle: string | null;
  connected: boolean;
  escrowLocked: boolean;
  escrowSessionId: string | null;
  /** Snake only — the player's current cash-out value in USD. */
  valueUsd?: number;
}

export interface AdminLiveRoom {
  roomId: string;
  game: string;
  clients: number;
  /** Absent when the room disposed between listing and snapshot. */
  phase?: string;
  stakeUsd?: number;
  uptimeMs?: number;
  tournamentMatchId?: string | null;
  customMatchId?: string | null;
  players?: AdminLivePlayer[];
}

export interface AdminLiveMatches {
  ok: boolean;
  /** False when the game-server was unreachable (rooms will be empty). */
  reachable: boolean;
  generatedAt: number;
  rooms: AdminLiveRoom[];
}

export type AdminResolveAction = "refund" | "award";

export interface AdminResolveResult {
  ok: boolean;
  /** refund_queued / award_queued / already_resolved / winner_not_found / … */
  outcome: string;
  detail?: string;
}

export const adminApi = {
  /** Caller's effective permissions (super-admin ⇒ all). */
  me: (token: string) => authedJson<AdminMe>(token, "/api/admin/me"),
  /** Live rooms on the game-server right now (players, stakes, phase). */
  liveMatches: (token: string) => authedJson<AdminLiveMatches>(token, "/api/admin/live-matches"),
  /** Force-resolve a live match: refund all stakes or award the pot. */
  resolveLiveMatch: (
    token: string,
    roomId: string,
    body: { action: AdminResolveAction; winnerSessionId?: string | null; reason: string },
  ) =>
    authedJson<AdminResolveResult>(token, `/api/admin/live-matches/${encodeURIComponent(roomId)}/resolve`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  /** Permission + role-preset catalog for the staff editor (super-admin only). */
  staffCatalog: (token: string) => authedJson<PermissionCatalog>(token, "/api/admin/staff/catalog"),
  listStaff: (token: string) => authedJson<{ staff: StaffMember[] }>(token, "/api/admin/staff"),
  setStaff: (token: string, wallet: string, permissions: Permission[], role: string | null) =>
    authedJson<{ ok: boolean; staff: StaffMember | null }>(token, `/api/admin/staff/${wallet}`, {
      method: "PUT",
      body: JSON.stringify({ permissions, role }),
    }),
  /** Admin map editor → commit the edited arena geometry to a branch + open a
   *  PR to main (super-admin only). */
  saveArena: (token: string, payload: { boxes: unknown[]; props: unknown[] }) =>
    authedJson<{ ok: boolean; prUrl: string; prNumber: number; branch: string }>(token, "/api/admin/arena", {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  removeStaff: (token: string, wallet: string) =>
    authedJson<{ ok: boolean; removed: boolean }>(token, `/api/admin/staff/${wallet}`, { method: "DELETE" }),
  users: (token: string, q = "", limit = 50, offset = 0) =>
    authedJson<{ users: AdminUserRow[]; total: number }>(
      token,
      `/api/admin/users?q=${encodeURIComponent(q)}&limit=${limit}&offset=${offset}`,
    ),
  suspend: (token: string, wallet: string, reason: string) =>
    authedJson<{ ok: boolean; wallet: string; suspended: boolean }>(
      token,
      `/api/admin/users/${wallet}/suspend`,
      { method: "POST", body: JSON.stringify({ reason }) },
    ),
  unsuspend: (token: string, wallet: string) =>
    authedJson<{ ok: boolean; wallet: string; suspended: boolean }>(
      token,
      `/api/admin/users/${wallet}/unsuspend`,
      { method: "POST" },
    ),
  userDetail: (token: string, wallet: string) =>
    authedJson<AdminUserDetail>(token, `/api/admin/users/${wallet}`),
  sendStaffMessage: (token: string, wallet: string, body: string) =>
    authedJson<{ ok: boolean; message: { id: string; createdAt: number } }>(
      token,
      `/api/admin/users/${wallet}/message`,
      { method: "POST", body: JSON.stringify({ body }) },
    ),
  reports: (token: string, status?: ReportStatus, limit = 50, offset = 0) =>
    authedJson<{ reports: AdminReport[]; total: number; openCount: number }>(
      token,
      `/api/admin/reports?${status ? `status=${status}&` : ""}limit=${limit}&offset=${offset}`,
    ),
  resolveReport: (
    token: string,
    id: string,
    body: { status: ReportStatus; resolution?: string; suspendTarget?: boolean },
  ) =>
    authedJson<{ ok: boolean; report: AdminReport; targetSuspended: boolean }>(
      token,
      `/api/admin/reports/${id}/resolve`,
      { method: "POST", body: JSON.stringify(body) },
    ),
  // ── Escrow break-glass (super-admin) ──
  escrowStatus: (token: string) => authedJson<EscrowAdminStatus>(token, "/api/admin/escrow/status"),
  escrowFreeze: (token: string, reason: string) =>
    authedJson<{ ok: boolean; softFreeze: EscrowFreezeState }>(token, "/api/admin/escrow/freeze", {
      method: "POST",
      body: JSON.stringify({ reason }),
    }),
  escrowUnfreeze: (token: string) =>
    authedJson<{ ok: boolean; softFreeze: EscrowFreezeState }>(token, "/api/admin/escrow/unfreeze", {
      method: "POST",
    }),
};

export interface EscrowFreezeState {
  frozen: boolean;
  reason: string;
  by: string;
  at: number;
}
export interface EscrowAdminStatus {
  network: string;
  softFreeze: EscrowFreezeState;
  onchain: { paused: boolean; treasury: string; totalLockedLamports: string } | { error: string } | null;
}

export const reportsApi = {
  create: (token: string, body: { target: string; reason: ReportReason; detail?: string }) =>
    authedJson<{ ok: boolean; id: string }>(token, "/api/reports", {
      method: "POST",
      body: JSON.stringify(body),
    }),
};

// ─── Admin tournament management ─────────────────────────────────────────────

/** One tournament instance as the admin dashboard sees it (all statuses). */
export interface AdminTournamentRow {
  id: string;
  templateId: string;
  game: TournamentGame;
  title: string;
  description: string;
  status: TournamentStatus;
  entry: number;
  bonus: number;
  feeBps: number;
  minPlayers: number;
  maxPlayers: number;
  playerCount: number;
  startsAt: number;
  roundIntervalSeconds: number;
  matchDeadlineSeconds: number;
  potUsd: number;
  champion: string | null;
  cancelReason: string | null;
  createdAtMs: number;
  settledAtMs: number | null;
  /** Whether DELETE is safe (terminal, or filling with no entrants). */
  removable: boolean;
}

export interface CreateAdminTournamentInput {
  game: TournamentGame;
  title: string;
  description?: string;
  entry: number;
  minPlayers: number;
  maxPlayers: number;
  /** Absolute draw time in ms (preferred). */
  startsAt?: number;
  startsInSeconds?: number;
  roundIntervalSeconds?: number;
  matchDeadlineSeconds?: number;
  feeBps?: number;
  /** Platform vault bonus in whole dollars (requires fund_tournaments). */
  bonus?: number;
}

export const adminTournamentsApi = {
  list: (token: string) =>
    authedJson<{ tournaments: AdminTournamentRow[]; escrowConfigured: boolean }>(
      token,
      "/api/admin/tournaments",
    ),
  create: (token: string, input: CreateAdminTournamentInput) =>
    authedJson<{ tournament: AdminTournamentRow }>(token, "/api/admin/tournaments", {
      method: "POST",
      body: JSON.stringify(input),
    }),
  reschedule: (token: string, id: string, startsAt: number) =>
    authedJson<{ tournament: AdminTournamentRow }>(token, `/api/admin/tournaments/${id}/schedule`, {
      method: "PATCH",
      body: JSON.stringify({ startsAt }),
    }),
  bulk: (token: string, action: "cancel" | "delete", ids: string[], reason?: string) =>
    authedJson<{
      action: string;
      results: { id: string; ok: boolean; error?: string }[];
      summary: { ok: number; failed: number };
    }>(token, "/api/admin/tournaments/bulk", {
      method: "POST",
      body: JSON.stringify({ action, ids, reason }),
    }),
  cancel: (token: string, id: string, reason?: string) =>
    authedJson<{ tournament: AdminTournamentRow }>(token, `/api/admin/tournaments/${id}/cancel`, {
      method: "POST",
      body: JSON.stringify({ reason }),
    }),
  remove: (token: string, id: string) =>
    authedJson<{ ok: boolean; removed: boolean }>(token, `/api/admin/tournaments/${id}`, {
      method: "DELETE",
    }),
};
